<template>
  <div>
    <static-alert :message="message" @on-hide="message=''" />
    <div class="chart-card">
      <h6 class="chart-title">Active Items by Listing Categories</h6>
      <div v-if="loading" class="d-flex justify-content-center align-items-center py-5">
        <loading-spinner />
      </div>
      <template v-else-if="chartData.length > 0">
        <apexchart
          type="bar"
          height="280"
          :options="chartOptions"
          :series="chartSeries"
        />
      </template>
      <div v-else class="text-muted text-center py-5">No data</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from "vue"
import { useStore } from "vuex"
import StaticAlert from "@/app/components/Utils/StaticAlert.vue"
import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue"

/**
 *     main_category_name,
 *     active_items,
 *     average_listing_price
 */

const apexchart = defineAsyncComponent(() =>
  import('vue3-apexcharts').then(module => module.default || module)
)

const store = useStore()
const message = ref('')
const loading = ref(false)
const chartData = ref([])
const CHART_COLORS = computed(() => store.state.chartColors)

const totalItems = computed(() => (chartData.value || []).reduce((sum, row) => sum + row.active_items, 0))
const orderedData = computed(() => {
  const sorted = [...(chartData.value || [])].sort((a, b) => b.active_items - a.active_items)
  const top = sorted.slice(0, 6)
  const rest = sorted.slice(6)
  if (rest.length === 0) return top
  const other = rest.reduce((acc, row) => ({
    main_category_name: 'Other',
    active_items: acc.active_items + row.active_items,
    average_listing_price: null
  }), { active_items: 0 })
  return [...top, other]
})

const chartSeries = computed(() => [{
  name: 'Items',
  data: orderedData.value.map(row => row.active_items)
}])

const chartOptions = computed(() => ({
  chart: {
    type: 'bar',
    toolbar: { show: false },
    animations: { enabled: true }
  },
  plotOptions: {
    bar: {
      barHeight: '95%',
      borderRadius: 2,
      horizontal: true,
      distributed: true,
      dataLabels: { position: 'top' }
    }
  },
  colors: Object.values(CHART_COLORS.value), //[CHART_COLORS.value.info, CHART_COLORS.value.danger, CHART_COLORS.value.success, CHART_COLORS.value.teal, CHART_COLORS.value.mint, CHART_COLORS.value.goldenrod, CHART_COLORS.value.yellow, CHART_COLORS.value.gray600],
  dataLabels: {
    enabled: true,
    textAnchor: 'start',
    offsetX: 15,
    style: {
      fontSize: '12px',
      colors: ['#000']
    },
    dropShadow: {
      enabled: true,
      top: 0,
      left: 0,
      blur: 2,
      color: '#fff',
      opacity: 1
    },
    formatter: (val) => {
      return `${val}  (${Math.round(parseFloat(val)/totalItems.value*100)}%)`
    }
  },
  xaxis: {
    categories: orderedData.value.map(row => row.main_category_name),
    // labels: {
    //   formatter: (val) => Math.round(val)
    // }
  },
  yaxis: {
    labels: {
      style: { fontSize: '12px' }
    }
  },
  tooltip: {
    y: {
      formatter: (val, { dataPointIndex }) => {
        const row = orderedData.value[dataPointIndex]
        const price = row?.average_listing_price != null ? `$${row.average_listing_price.toFixed(2)}` : 'N/A'
        return `${val} items (Average Listing Price: ${price})`
      }
    }
  },
  legend: { show: false },
  grid: {
    borderColor: '#e0e0e0',
    strokeDashArray: 4
  }
}))

onMounted(() => {
  fetchData()
})

function fetchData() {
  loading.value = true
  store.dispatch("eshop/getItemAggregateCategory")
    .then(data => {
      chartData.value = data.chart_data || []
    })
    .catch(error => {
      message.value = error.response?.data?.error || error.message || 'Failed to load inventory category chart data'
      console.error('Failed to load inventory category chart data:', error)
    })
    .finally(() => {
      loading.value = false
    })
}
</script>
